// Auto-tone hero images: sample the darker (left) side of a hero photo,
// compute perceived luminance, and derive CSS `filter` values so the photo
// stays visible under the charcoal gradient overlay without manual editing.
//
// Runs once per URL — results are cached in-memory. Safe for SSR/first paint:
// callers get a neutral filter until the sample resolves.

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

// Target perceived luminance for the left half of the hero (where the H1 sits).
// Overlay darkens by ~40-65% on that side, so the raw pixels should land ~150
// to remain readable-but-not-blown-out after the gradient.
const TARGET_LUMA = 150;
const NEUTRAL = "brightness(1) contrast(1) saturate(1)";

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

/** Perceived luminance (Rec. 709) for one RGB pixel, 0-255. */
const luma = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

const analyze = (src: string): Promise<string> => {
  const hit = cache.get(src);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(src);
  if (pending) return pending;

  const p = new Promise<string>((resolve) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      resolve(NEUTRAL);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onerror = () => {
      cache.set(src, NEUTRAL);
      resolve(NEUTRAL);
    };
    img.onload = () => {
      try {
        // Downsample to a tiny canvas — a 40×24 sample is plenty for a mean.
        const W = 40;
        const H = 24;
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          cache.set(src, NEUTRAL);
          resolve(NEUTRAL);
          return;
        }
        ctx.drawImage(img, 0, 0, W, H);
        // Sample the LEFT ~55% — that's where the headline overlays sit and
        // where visibility matters most against the strongest gradient stop.
        const sampleW = Math.floor(W * 0.55);
        const { data } = ctx.getImageData(0, 0, sampleW, H);
        let sum = 0;
        let stdSum = 0;
        const px = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          sum += luma(data[i]!, data[i + 1]!, data[i + 2]!);
        }
        const mean = sum / px;
        // Second pass for contrast estimation (stddev of luma).
        for (let i = 0; i < data.length; i += 4) {
          const d = luma(data[i]!, data[i + 1]!, data[i + 2]!) - mean;
          stdSum += d * d;
        }
        const std = Math.sqrt(stdSum / px);

        // Brightness: nudge mean toward target. Bounded so we never wash out
        // an already-bright photo or push a very dark one past a natural look.
        const brightness = clamp(TARGET_LUMA / Math.max(mean, 40), 0.9, 1.6);

        // Contrast: low-stddev (flat/foggy) photos read as mush under the
        // overlay — bump them; high-contrast photos stay as-is.
        const contrast = clamp(1 + (55 - std) / 220, 0.95, 1.18);

        // Slight saturation nudge to counter the wash from the brightness
        // multiplier — kept small so photos still look natural.
        const saturate = clamp(0.95 + (brightness - 1) * 0.35, 0.95, 1.15);

        const filter = `brightness(${brightness.toFixed(3)}) contrast(${contrast.toFixed(3)}) saturate(${saturate.toFixed(3)})`;
        cache.set(src, filter);
        resolve(filter);
      } catch {
        // Cross-origin taint or any decode error — leave the image untouched.
        cache.set(src, NEUTRAL);
        resolve(NEUTRAL);
      }
    };
    img.src = src;
  }).finally(() => {
    inflight.delete(src);
  });

  inflight.set(src, p);
  return p;
};

/** Synchronously read a cached filter (or `null` when it hasn't been computed). */
export const readCachedHeroFilter = (src: string): string | null =>
  cache.get(src) ?? null;

/** Kick off (or return cached) auto-tone analysis for a single URL. */
export const computeHeroFilter = (src: string): Promise<string> => analyze(src);

export const NEUTRAL_HERO_FILTER = NEUTRAL;
